/**
 * User Edit Panel
 * Sheet panel for editing a user's details
 */

import { useEffect, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type { ManagedUser, UpdateUserRequest, UserRole, UserStatus } from '@/types/user-management'

// ============================================================================
// COMPONENT
// ============================================================================

interface UserEditPanelProps {
  user: ManagedUser | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: UpdateUserRequest) => void
  isPending: boolean
}

export function UserEditPanel({
  user,
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: UserEditPanelProps) {
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<UserRole>('USER')
  const [status, setStatus] = useState<UserStatus>('ACTIVE')
  const [error, setError] = useState<string | null>(null)

  // Sync state with selected user
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName)
      setRole(user.role)
      setStatus(user.status)
      setError(null)
    }
  }, [user])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!displayName.trim()) {
      setError('Display name is required')
      return
    }
    onSubmit({ displayName: displayName.trim(), role, status })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Edit User</SheetTitle>
          <SheetDescription>{user?.email ?? ''}</SheetDescription>
        </SheetHeader>

        <div className="py-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                placeholder="Display name"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setError(null) }}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>

            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as UserStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                  <SelectItem value="SUSPENDED">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <SheetFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </SheetFooter>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  )
}
